import { useState } from 'react';
import Navbar from '@/pages/home/components/Navbar';
import FooterSection from '@/pages/home/components/FooterSection';
import QuotePopup from '@/pages/home/components/QuotePopup';

const offices = [
  {
    city: 'Guangzhou HQ',
    address: 'Room 906, Zhanghe Shengde Building, Huangpu Avenue, Tianhe District, Guangzhou City, Guangdong Province, China',
    phone: '+86 17671262710',
    email: 'support@steelink.com',
    hours: 'Mon–Fri: 9:00 AM – 6:00 PM (CST)',
    icon: 'ri-building-2-line',
  },
  {
    city: 'WhatsApp (24/7)',
    address: 'Available worldwide via WhatsApp for urgent inquiries and order tracking.',
    phone: '+86 17671262710',
    email: 'support@steelink.com',
    hours: 'Available 24/7 for urgent queries',
    icon: 'ri-whatsapp-line',
  },
];

const faqs = [
  { q: 'What is your minimum order quantity (MOQ)?', a: 'Our MOQ is typically 25 MT per product. For mixed container orders, we can accommodate smaller quantities — contact us to discuss.' },
  { q: 'Do you offer FOB pricing?', a: 'Yes. We offer FOB pricing from major Chinese ports including Tianjin, Shanghai, Guangzhou, and Qingdao.' },
  { q: 'How long does delivery take?', a: 'Production lead time is 7–15 days after deposit. Shipping time varies by destination — typically 15–45 days by sea.' },
  { q: 'Can you provide mill test certificates?', a: 'Yes. All products come with mill test certificates (MTC) and can be third-party inspected by SGS, BV, or TUV upon request.' },
];

export default function ContactPage() {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch('https://readdy.ai/api/form/d778kc2bj7ht1ofp4g2g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitStatus('success');
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar onQuoteClick={() => setQuoteOpen(true)} />
      <QuotePopup isOpen={quoteOpen} onClose={() => setQuoteOpen(false)} />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gray-900 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: "url('https://readdy.ai/api/search-image?query=steel%20factory%20industrial%20production%20facility%20aerial%20view%20professional%20photography%20dramatic%20lighting&width=1400&height=500&seq=contact-hero-1&orientation=landscape')" }}
        />
        <div className="relative max-w-[1420px] mx-auto px-6 text-center">
          <span className="inline-block px-3 py-1 bg-yellow-400 text-gray-900 text-xs font-black rounded mb-4 tracking-widest uppercase">Get In Touch</span>
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4">Contact Steelinkglobal</h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Ready to source quality steel from China? Our team responds within 2 hours during business hours.
          </p>
        </div>
      </section>

      {/* Contact Cards */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-[1420px] mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 border border-gray-100 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-yellow-400 rounded-full mx-auto mb-4">
                <i className="ri-phone-line text-xl text-gray-900" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Call Us</h3>
              <a href="tel:+8617671262710" className="text-yellow-600 font-semibold hover:underline">+86 17671262710</a>
              <p className="text-gray-500 text-sm mt-1">Mon–Fri, 9AM–6PM CST</p>
            </div>
            <div className="bg-white rounded-lg p-6 border border-gray-100 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-green-500 rounded-full mx-auto mb-4">
                <i className="ri-whatsapp-line text-xl text-white" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">WhatsApp</h3>
              <a href="https://wa.me/8617671262710" target="_blank" rel="noopener noreferrer" className="text-green-600 font-semibold hover:underline">+86 17671262710</a>
              <p className="text-gray-500 text-sm mt-1">Available 24/7</p>
            </div>
            <div className="bg-white rounded-lg p-6 border border-gray-100 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-yellow-400 rounded-full mx-auto mb-4">
                <i className="ri-mail-line text-xl text-gray-900" />
              </div>
              <h3 className="font-bold text-gray-900 mb-1">Email Us</h3>
              <a href="mailto:support@steelink.com" className="text-yellow-600 font-semibold hover:underline">support@steelink.com</a>
              <p className="text-gray-500 text-sm mt-1">Reply within 2 hours</p>
            </div>
          </div>
        </div>
      </section>

      {/* Form + Info */}
      <section className="py-16">
        <div className="max-w-[1420px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Form */}
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-2">Send Us a Message</h2>
            <p className="text-gray-500 text-sm mb-8">Fill in the form and our team will get back to you within 2 business hours.</p>

            {submitStatus === 'success' ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-2xl text-green-600" />
                </div>
                <h3 className="font-bold text-gray-900 text-lg mb-2">Message Sent!</h3>
                <p className="text-gray-500 text-sm">Thank you for reaching out. We&apos;ll respond within 2 business hours.</p>
                <button onClick={() => setSubmitStatus('idle')} className="mt-4 px-6 py-2 bg-yellow-400 text-gray-900 font-bold rounded-md text-sm cursor-pointer whitespace-nowrap">Send Another</button>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Full Name *</label>
                    <input name="full_name" required type="text" placeholder="John Smith" className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Company Name</label>
                    <input name="company" type="text" placeholder="ABC Construction Ltd" className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-400" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Email Address *</label>
                    <input name="email" required type="email" placeholder="john@company.com" className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-400" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Phone / WhatsApp</label>
                    <input name="phone" type="tel" placeholder="+1 555 000 0000" className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Country / Region *</label>
                  <input name="country" required type="text" placeholder="e.g. United States, Nigeria, UAE" className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-400" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Subject</label>
                  <select name="subject" className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-400 cursor-pointer">
                    <option value="Product Inquiry">Product Inquiry</option>
                    <option value="Request a Quote">Request a Quote</option>
                    <option value="Order Status">Order Status</option>
                    <option value="Technical Question">Technical Question</option>
                    <option value="Partnership">Partnership / Distributor</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Message *</label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Tell us what products you need, quantities, destination port, and any other details..."
                    className="w-full border border-gray-200 rounded-md px-4 py-2.5 text-sm focus:outline-none focus:border-yellow-400 resize-none"
                  />
                  <p className="text-xs text-gray-400 mt-1">Max 500 characters</p>
                </div>
                {submitStatus === 'error' && (
                  <p className="text-red-500 text-sm">Something went wrong. Please try again or contact us via WhatsApp.</p>
                )}
                <button type="submit" className="w-full bg-yellow-400 text-gray-900 font-black py-3 rounded-md text-sm hover:bg-yellow-500 transition-colors cursor-pointer whitespace-nowrap">
                  Send Message <i className="ri-send-plane-line ml-2" />
                </button>
              </form>
            )}
          </div>

          {/* Info */}
          <div className="space-y-8">
            <div>
              <h2 className="text-2xl font-black text-gray-900 mb-6">Our Office</h2>
              {offices.map((o) => (
                <div key={o.city} className="flex gap-4 mb-6 p-5 bg-gray-50 rounded-lg border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center bg-yellow-400 rounded-lg flex-shrink-0">
                    <i className={`${o.icon} text-lg text-gray-900`} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">{o.city}</h3>
                    <p className="text-gray-500 text-sm mb-1">{o.address}</p>
                    <p className="text-sm"><i className="ri-phone-line mr-1 text-yellow-500" /><a href={`tel:${o.phone}`} className="text-gray-700 hover:text-yellow-600">{o.phone}</a></p>
                    <p className="text-sm"><i className="ri-time-line mr-1 text-yellow-500" /><span className="text-gray-500">{o.hours}</span></p>
                  </div>
                </div>
              ))}
            </div>

            {/* Map */}
            <div>
              <h3 className="font-bold text-gray-900 mb-3">Find Us on the Map</h3>
              <div className="rounded-lg overflow-hidden border border-gray-200 h-56">
                <iframe
                  title="Steelinkglobal Office Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3651.9!2d113.3!3d23.1!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjPCsDA2JzAwLjAiTiAxMTPCsDE4JzAwLjAiRQ!5e0!3m2!1sen!2scn!4v1700000000000!5m2!1sen!2scn"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* FAQ */}
            <div>
              <h3 className="font-bold text-gray-900 mb-4">Quick FAQs</h3>
              <div className="space-y-3">
                {faqs.map((faq) => (
                  <details key={faq.q} className="group bg-gray-50 rounded-lg border border-gray-100">
                    <summary className="flex items-center justify-between px-4 py-3 cursor-pointer text-sm font-semibold text-gray-800 list-none">
                      {faq.q}
                      <i className="ri-add-line group-open:hidden text-yellow-500 flex-shrink-0 ml-2" />
                      <i className="ri-subtract-line hidden group-open:block text-yellow-500 flex-shrink-0 ml-2" />
                    </summary>
                    <div className="px-4 pb-3 text-sm text-gray-500">{faq.a}</div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-14 bg-yellow-400">
        <div className="max-w-[1420px] mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl font-black text-gray-900">Need a Fast Quote?</h2>
            <p className="text-gray-800 text-sm mt-1">Tell us your product, quantity and destination — we&apos;ll respond in under 2 hours.</p>
          </div>
          <button onClick={() => setQuoteOpen(true)} className="px-8 py-3 bg-gray-900 text-white font-black rounded-md text-sm hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
            Get Instant Quote <i className="ri-arrow-right-line ml-2" />
          </button>
        </div>
      </section>

      <FooterSection />
    </div>
  );
}
